
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SettingsLayout } from '../../components/SettingsLayout';
import { Loader2, Check } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { resolveAuthUser } from '../../lib/utils';

export const GeneralInfoScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const authUser = useMemo(() => resolveAuthUser(), []);
  const displayName = authUser?.nickname || authUser?.username || authUser?.email || authUser?.phone || '';
  const emailValue = authUser?.email || '';
  const avatarText = displayName ? displayName.trim().charAt(0).toUpperCase() : '';
  const avatarUrl = authUser?.avatar || '';

  const handleSave = () => {
    setLoading(true);
    setTimeout(() => {
        setLoading(false);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 2000);
    }, 1500);
  };

  return (
    <SettingsLayout>
      <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden p-10 relative">
            <div className="absolute top-10 right-10 flex flex-col items-end gap-1">
                <span className="px-2.5 py-0.5 bg-blue-50 text-primary text-[11px] font-bold rounded border border-blue-100 uppercase tracking-wider">{t('generalInfo.proPlan')}</span>
                <button onClick={() => navigate('/settings/subscription')} className="text-xs font-medium text-primary hover:underline hover:text-blue-700 transition-colors">{t('generalInfo.upgradePlan')}</button>
            </div>
            <header className="mb-10">
                <h1 className="text-2xl font-semibold text-slate-900">{t('generalInfo.title')}</h1>
                <p className="text-slate-500 text-sm mt-1">{t('generalInfo.subtitle')}</p>
            </header>
            <div className="space-y-10">
                <div className="flex items-center gap-8">
                    <div className="w-24 h-24 rounded-full overflow-hidden border border-slate-100 shadow-sm shrink-0 bg-slate-100 text-slate-700 flex items-center justify-center text-2xl font-bold">
                        {avatarUrl ? (
                          <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
                        ) : avatarText ? (
                          <span>{avatarText}</span>
                        ) : (
                          <span className="material-symbols-outlined text-3xl">person</span>
                        )}
                    </div>
                    <div className="space-y-3">
                        <div className="flex gap-3">
                            <button className="text-sm font-semibold px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm active:scale-95">{t('generalInfo.uploadNew')}</button>
                            <button className="text-sm font-semibold px-4 py-2 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-all active:scale-95">{t('generalInfo.remove')}</button>
                        </div>
                        <p className="text-xs text-slate-400">{t('generalInfo.photoHint')}</p>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">{t('generalInfo.fullName')}</label>
                        <input className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/10 focus:border-primary outline-none transition-all placeholder:text-slate-400 text-slate-900" placeholder={t('generalInfo.fullNamePlaceholder')} type="text" defaultValue={displayName}/>
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">{t('generalInfo.professionalEmail')}</label>
                        <input className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/10 focus:border-primary outline-none transition-all placeholder:text-slate-400 text-slate-900" placeholder={t('generalInfo.emailPlaceholder')} type="email" defaultValue={emailValue}/>
                    </div>
                    <div className="space-y-2 col-span-1 md:col-span-2">
                        <label className="text-sm font-medium text-slate-700">{t('generalInfo.companyName')}</label>
                        <input className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/10 focus:border-primary outline-none transition-all placeholder:text-slate-400 text-slate-900" placeholder={t('generalInfo.companyPlaceholder')} type="text" defaultValue=""/>
                    </div>
                </div>
                <div className="pt-8 border-t border-slate-100 flex items-center justify-end gap-6">
                    <button className="text-sm font-medium text-slate-500 hover:text-slate-800 transition-all hover:underline">{t('generalInfo.cancel')}</button>
                    <button
                        onClick={handleSave}
                        disabled={loading}
                        className={`px-8 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-sm flex items-center gap-2 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed ${success ? 'bg-emerald-500 text-white' : 'bg-primary text-white hover:bg-blue-700 hover:shadow-md'}`}
                    >
                        {loading ? <Loader2 className="animate-spin" size={16} /> : success ? <Check size={16} /> : null}
                        {loading ? t('generalInfo.saving') : success ? t('generalInfo.savedChanges') : t('generalInfo.saveChanges')}
                    </button>
                </div>
            </div>
        </div>
        <div className="mt-6 text-center">
            <p className="text-slate-400 text-[11px] uppercase tracking-widest font-medium">{t('appName')} {t('generalInfo.footer')}</p>
        </div>
      </div>
    </SettingsLayout>
  );
};
